import React, { useState, useEffect } from 'react';
import { tekmetricService } from '../../services/api';
import { wsService } from '../../services/websocket-simple';

interface LocationData {
  location_code: string;
  location_name: string;
  data: {
    kpis: {
      daily_revenue: number;
      daily_jobs: number;
      average_ro: number;
    };
  };
  synced_at: string;
  status: string;
}

interface TekMetricData {
  [key: string]: LocationData;
}

export const TekMetricDashboard: React.FC = () => {
  const [data, setData] = useState<TekMetricData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Subscribe to WebSocket updates
  useEffect(() => {
    const handleSyncUpdate = (message: any) => {
      console.log('TekMetric sync update received:', message);
      setLastSync(new Date());
      if (message.data) {
        setData(message.data);
        setSyncStatus('success');
        setTimeout(() => setSyncStatus('idle'), 3000);
      }
    };

    const handleTekMetricData = (message: any) => {
      console.log('TekMetric data received:', message);
      if (message.data) {
        setData(message.data);
      }
    };

    wsService.on('tekmetric_sync_update', handleSyncUpdate);
    wsService.on('tekmetric_data', handleTekMetricData);

    return () => {
      wsService.off('tekmetric_sync_update', handleSyncUpdate);
      wsService.off('tekmetric_data', handleTekMetricData);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await tekmetricService.getData();
      if (response.success && response.data) {
        setData(response.data);
        setLastSync(new Date());
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load TekMetric data');
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async (location?: string) => {
    try {
      setSyncStatus('syncing');
      await tekmetricService.triggerSync(location, true);
      setSyncStatus('success');
      setTimeout(() => {
        loadData();
        setSyncStatus('idle');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to trigger sync');
      setSyncStatus('error');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadData}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const locations = data ? Object.keys(data) : [];
  const displayData = selectedLocation === 'all'
    ? data
    : data && selectedLocation in data
    ? { [selectedLocation]: data[selectedLocation] }
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">TekMetric Live Data</h2>
          {lastSync && (
            <p className="text-sm text-gray-600">
              Last synced: {lastSync.toLocaleString()}
            </p>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Sync Status Indicator */}
          {syncStatus !== 'idle' && (
            <div className="flex items-center space-x-2">
              {syncStatus === 'syncing' && (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-gray-600">Syncing...</span>
                </>
              )}
              {syncStatus === 'success' && (
                <span className="text-sm text-green-600">✓ Synced</span>
              )}
              {syncStatus === 'error' && (
                <span className="text-sm text-red-600">✗ Error</span>
              )}
            </div>
          )}

          {/* Manual Sync Button */}
          <button
            onClick={() => triggerSync(selectedLocation === 'all' ? undefined : selectedLocation)}
            disabled={syncStatus === 'syncing'}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            🔄 Sync Now
          </button>
        </div>
      </div>

      {/* Location Filter */}
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium text-gray-700">Location:</label>
        <select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Locations</option>
          {locations.map((loc) => (
            <option key={loc} value={loc}>
              {data![loc].location_name} ({loc})
            </option>
          ))}
        </select>
      </div>

      {/* Data Cards */}
      {displayData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(displayData).map(([code, location]) => (
            <div key={code} className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
              {/* Location Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {location.location_name}
                </h3>
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                  {code}
                </span>
              </div>

              {/* KPIs */}
              {location.status === 'success' && location.data?.kpis ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600 font-medium">Daily Revenue</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(location.data.kpis.daily_revenue)}
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-600 font-medium">Daily Jobs</p>
                    <p className="text-2xl font-bold text-green-900">
                      {location.data.kpis.daily_jobs}
                    </p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-purple-600 font-medium">Average RO</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {formatCurrency(location.data.kpis.average_ro)}
                    </p>
                  </div>

                  <div className="text-xs text-gray-500 text-center pt-2 border-t">
                    Updated: {formatTime(location.synced_at)}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>⚠️ {location.status === 'error' ? 'Sync Error' : 'No Data'}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* WebSocket Connection Status */}
      <div className="text-xs text-gray-500 text-center">
        <span className="inline-flex items-center space-x-1">
          <span className={`h-2 w-2 rounded-full ${wsService.isConnected() ? 'bg-green-500' : 'bg-red-500'}`}></span>
          <span>{wsService.isConnected() ? 'Live updates active' : 'Connecting...'}</span>
        </span>
      </div>
    </div>
  );
};

export default TekMetricDashboard;
